import React, { SetStateAction } from 'react';
import { uniq, compact, sortBy, keys } from 'lodash';
import * as firebase from 'firebase/app';
import { Command, BoardState, User } from '../types';
import { getUser } from '../helpers/gameServer';

const useRoomDataToListenFor = <P>({
    roomId,
    initialState,
    fbRef,
    formatter,
    eventType,
}: {
    roomId: string,
    initialState: P,
    fbRef: firebase.database.Reference | firebase.database.Query,
    formatter?: (snapshot: firebase.database.DataSnapshot) => SetStateAction<P>,
    eventType: firebase.database.EventType,
}) => {
    // Dispatch<SetStateAction<S>> is actually (value: S | ((prevState: S) => S)) => void. It
    const [roomDataToListenFor, setRoomDataToListenFor] = React.useState<P>(initialState);
    const [success, setSuccess] = React.useState(false);

    const fbRefRef = React.useRef(fbRef);

    // Reset state when the room changes
    React.useEffect(() => {
        setSuccess(false);
        setRoomDataToListenFor(initialState);
    }, [roomId, initialState]);

    React.useEffect(() => {
        const fbRef = fbRefRef.current;
        console.log({ eventType });
        const cb = fbRef.on(eventType, (snapshot) => {
            const value = snapshot.val();
            if (value) {
                if (formatter) {
                    setRoomDataToListenFor(formatter(snapshot));
                } else {
                    setRoomDataToListenFor(value);
                }
            }
            setSuccess(true);
        });
        return () => fbRef.off(eventType, cb);
    }, [roomId, eventType, formatter]);

    console.log({ eventType });

    return {
        success,
        roomDataToListenFor,
    };
};

export const useGameHasStarted = (roomId: string) => {
    const [gameHasStarted, setGameHasStarted] = React.useState(false);
    const [success, setSuccess] = React.useState(false);

    // Reset state when the room changes
    React.useEffect(() => {
        setSuccess(false);
        setGameHasStarted(false);
    }, [roomId]);

    React.useEffect(() => {
        const database = firebase.database();
        const commandsRef = database.ref(`rooms/${roomId}/commands`).limitToFirst(1);
        const cb = commandsRef.on('value', (snapshot) => {
            const value = snapshot.val();
            if (value) {
                setGameHasStarted(true);
            }
            setSuccess(true);
        });
        return () => commandsRef.off('value', cb);
    }, [roomId, gameHasStarted]);
    return { gameHasStarted, success };
    /*
    const { success, roomDataToListenFor: gameHasStarted } = useRoomDataToListenFor({
        roomId,
        fbRef: firebase.database().ref(`rooms/${roomId}/commands`).limitToFirst(1),
        eventType: 'value',
        initialState: false,
    });
    return { gameHasStarted: !!gameHasStarted, success };
     */
};

export const useGetUsers = (roomId: string) => {
    const [users, setUsers] = React.useState<User[]>([]);
    const [success, setSuccess] = React.useState<boolean>(false);

    // Reset state when the room changes
    React.useEffect(() => {
        setSuccess(false);
        setUsers([]);
    }, [roomId]);

    React.useEffect(() => {
        const database = firebase.database();
        const usersRef = database.ref(`rooms/${roomId}/users`);

        const cb = (snapshot: firebase.database.DataSnapshot) => {
            const value: { [userId: string]: string } = snapshot.val();
            const sortedKeys = compact(uniq(sortBy(keys(value))));
            const sortedUsers = sortedKeys.map((key) => ({
                userName: value[key],
                userId: key,
            }));
            setUsers(sortedUsers);
            setSuccess(true);
        };

        usersRef.on('value', cb);

        return () => usersRef.off('value', cb);
    }, [roomId]);
    return { users, success };
};

export const useGetUser = (roomId: string, userId: string) => {
    const [user, setUser] = React.useState<User>();

    // Reset state when the room changes
    React.useEffect(() => {
        setUser(undefined);
    }, [roomId]);

    React.useEffect(() => {
        const database = firebase.database();
        const userRef = database.ref(`rooms/${roomId}/users/${userId}`);

        const cb = (snapshot: firebase.database.DataSnapshot) => {
            const userName: string = snapshot.val();
            setUser({ userId, userName });
        };

        userRef.on('value', cb);

        return () => userRef.off('value', cb);
    }, [userId, roomId]);

    return { user };
};

export const useCreateRoom = (userId: string, userName: string, roomId: string) => {
    React.useEffect(() => {
        if (userName && roomId) {
            const database = firebase.database();
            const roomRef = database.ref(`rooms/${roomId}`);
            roomRef.once('value').then((snapshot) => {
                if (!snapshot.val()) {
                    roomRef.set({
                        dateCreated: new Date().getTime(),
                        users: { [userId]: userName },
                    });
                } else {
                    roomRef.child('users').update({ [userId]: userName });
                }
            });
        }

        // Create or join existing room
    }, [userId, userName, roomId]);
};

type UseCommandType = { command: Command, id: string }[];

const commandsRet: UseCommandType = [];
const init: UseCommandType = [];

export const useCommands = (roomId: string) => {
    const [commands, setCommands] = React.useState<{ command: Command, id: string }[]>([]);

    // Reset state when the room changes
    React.useEffect(() => {
        setCommands([]);
    }, [roomId]);

    React.useEffect(() => {
        const database = firebase.database();
        const usersRef = database.ref(`rooms/${roomId}/commands`);
        const cb = usersRef.on('child_added', (snapshot) => {
            const command: Command = snapshot.val();
            const id = snapshot.key;
            if (id) {
                setCommands((commands) => [...commands, { command, id }]);
            }
        });
        return () => usersRef.off('child_added', cb);
    }, [roomId]);

    return {
        commands,
    };
    /*
    const formatter = React.useCallback(
        (snapshot) => (commands: any) => {
            const id = snapshot.key;
            const command: Command = snapshot.val();
            if (id) {
                return [...commands, { command, id }];
            } else {
                return commands;
            }
        },
        [],
    );
    const { roomDataToListenFor: commands } = useRoomDataToListenFor<UseCommandType>({
        roomId,
        initialState: init,
        fbRef: firebase.database().ref(`rooms/${roomId}/commands`),
        eventType: 'child_added',
        //formatter,
    });

    return {
        commands: commandsRet,
    };

 */
};

export const useSubmittedBoards = (roomId: string) => {
    const [submittedBoards, setSubmittedBoards] = React.useState<{
        [userId: string]: BoardState,
    }>();

    const [success, setSuccess] = React.useState(false);

    // Reset state when the room changes
    React.useEffect(() => {
        setSubmittedBoards({});
        setSuccess(false);
    }, [roomId]);

    React.useEffect(() => {
        const database = firebase.database();
        const boardsRef = database.ref(`rooms/${roomId}/board`);
        const cb = boardsRef.on('value', (snapshot) => {
            const board: { [userId: string]: BoardState } = snapshot.val();
            const id = snapshot.key;
            if (id) {
                setSubmittedBoards(board);
            }
            setSuccess(true);
        });
        return () => boardsRef.off('value', cb);
    }, [roomId]);

    return {
        submittedBoards,
        success,
    };
    /*
    const { success, roomDataToListenFor: submittedBoards } = useRoomDataToListenFor<{
        [id: string]: BoardState,
    }>({
        roomId,
        fbRef: firebase.database().ref(`rooms/${roomId}/board`),
        eventType: 'value',
        initialState: {},
    });
     */
};

type HandSizes = { [userId: string]: number };

export const useGetHandSizes = (roomId: string) => {
    const [handSizes, setHandSizes] = React.useState<HandSizes>();

    // Reset state when the room changes
    React.useEffect(() => {
        setHandSizes(undefined);
    }, [roomId]);

    React.useEffect(() => {
        const database = firebase.database();
        const handSizesRef = database.ref(`rooms/${roomId}/handSizes`);

        const updateHandSize = (snapshot: firebase.database.DataSnapshot) => {
            const userId = snapshot.key;
            const handSize = snapshot.val();
            if (userId) {
                setHandSizes((handSizes) => ({
                    ...handSizes,
                    [userId]: handSize,
                }));
            }
        };

        handSizesRef.on('child_changed', updateHandSize);
        handSizesRef.on('child_added', updateHandSize);

        return () => {
            handSizesRef.off('child_changed', updateHandSize);
            handSizesRef.off('child_added', updateHandSize);
        };
    }, [roomId]);

    return { handSizes };
};

export const useWinner = (roomId: string) => {
    const [winner, setWinner] = React.useState<User>();
    const [success, setSuccess] = React.useState(false);

    // Reset state when the room changes
    React.useEffect(() => {
        setWinner(undefined);
        setSuccess(false);
    }, [roomId]);

    React.useEffect(() => {
        const database = firebase.database();
        const winnerRef = database.ref(`rooms/${roomId}/winner`);
        const cb = winnerRef.on('value', (snapshot) => {
            const userId = snapshot.val();
            if (userId) {
                getUser({ roomId, userId }).then((user) => {
                    setWinner(user);
                });
            }
            setSuccess(true);
        });
        return () => winnerRef.off('value', cb);
    }, [roomId]);

    return {
        winner,
        success,
    };
    /*
    const { success, roomDataToListenFor: winner } = useRoomDataToListenFor<string>({
        roomId,
        fbRef: firebase.database().ref(`rooms/${roomId}/winner`),
        eventType: 'value',
        initialState: '',
    });

    return { winner, success };
     */
};

